'use client';

import CoverImage from '@/components/cover-image';
import { useUserData } from '@/context/auth/user-data-context';
import { LanguageIsoCode, type Program } from '@/graphql/graphql';
import { Link, useRouter } from '@/i18n/navigation';
import { isoEn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { IconFileWarning, IconNoPublish } from '../../../components/ui/icons';

interface MediaCardProps {
    program: Program;
    language: LanguageIsoCode;
}

export default function Mediacard({ program, language }: MediaCardProps) {
    const t = useTranslations('MediaCard');
    const router = useRouter();
    const locale = (language ? language.toLowerCase() : isoEn) as LanguageIsoCode;
    const { isSeller, isBuyer } = useUserData();

    const gotoCategory = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();
        router.push({
            pathname: '/programs',
            query: { category: program.category.id },
        });
    };

    const href = {
        pathname: '/programs/[id]' as const,
        params: { id: String(program.identifier.id) },
    };

    return (
        <Link href={href} className="block relative aspect-3/4 @container/mediacard p-1 group cursor-pointer">
            <div
                className={`absolute rounded-[8px] inset-0 transition-transform duration-300 bg-transparent ${program.validated !== false && program.published !== false ? 'ease-in-out group-hover:scale-115 group-hover:z-20' : ''}`}
            >
                <div className="flex flex-col items-end gap-[5px] absolute z-1 top-[10px] right-[10px]">
                    {isBuyer && (program.licensesQuantity ?? 0) > 0 && (
                        <div
                            className={`
                            text-black font-medium text-[4.8cqw]
                            px-[3.5cqw] py-[1.7cqw]
                            rounded-sm bg-white/80
                            opacity-100 transition-all duration-300
                            group-hover:opacity-0`}
                        >
                            {t('alreadyBought')}
                        </div>
                    )}
                    {isSeller &&
                        program.validated !== false &&
                        program.published !== false &&
                        (program.licensesQuantity ?? 0) > 0 && (
                            <div
                                className={`
                                text-black text-[4.8cqw] font-medium
                                px-[3.5cqw] py-[1.7cqw]
                                rounded-sm bg-white/80
                                opacity-100 transition-all duration-300
                                group-hover:opacity-0`}
                            >
                                {program.licensesQuantity === 1
                                    ? `${program.licensesQuantity} ${t('OnelicenseSold')}`
                                    : `${program.licensesQuantity} ${t('licensesSold')}`}
                            </div>
                        )}
                </div>

                {/* IMAGE */}

                <CoverImage
                    program={program}
                    className={`w-full h-full ${
                        program.validated === false || program.published === false ? 'opacity-20' : ''
                    }`}
                    fallbackTitle={true}
                />
                {/* OVERLAY TEXTE */}
                {program.validated !== false && program.published !== false && (
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <div className="absolute bottom-0 left-0 w-full flex flex-col p-[7.2cqw] bg-[#1F1F1F] text-white text-left rounded-b-[8px]">
                            {program.episodesQuantity && program.episodesQuantity > 0 ? (
                                <div className="absolute right-[6.9cqw] -top-[17.4cqw] px-[3.5cqw] py-[1.7cqw] text-[4.8cqw] text-white bg-[#1F1F1F] rounded-sm font-medium">
                                    {program.episodesQuantity} {t('episodes')}
                                </div>
                            ) : undefined}

                            <p className="text-[7.2cqw] font-bold mb-[0.4em]">
                                <span className="line-clamp-4 w-full max-w-full">
                                    {program.titles[locale as LanguageIsoCode]}
                                </span>
                            </p>
                            {program.firstBroadcastDate ? (
                                <p className="text-[6.5cqw] mb-[1em]">
                                    {new Date(program.firstBroadcastDate).getFullYear()}
                                </p>
                            ) : undefined}
                            <p className="text-[5.2cqw] line-clamp-3 mb-[2em] min-h-[4.5em]">
                                {program.synopsis[locale]}
                            </p>

                            <div
                                onClick={gotoCategory}
                                onKeyUp={gotoCategory}
                                className="text-[4.8cqw] underline underline-offset-1 mr-auto"
                            >
                                {program.category.title}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {program.validated === false && (
                <div className="absolute inset-0 flex flex-col items-center justify-center uppercase text-[7.2cqw] font-bold">
                    <IconFileWarning className="text-[#D91E1E] mb-2 w-[2em] h-[2em]" />
                    <p className="text-center text-shadow-lg">{t('CheckAndValidate')}</p>
                </div>
            )}
            {program.validated === true && program.published === false && (
                <div className="absolute inset-0 flex flex-col items-center justify-center uppercase text-[7.2cqw] font-bold">
                    <IconNoPublish className="text-[#D91E1E] mb-2 w-[2em] h-[2em]" />
                    <p className="text-center text-shadow-lg">{t('Unpublished')}</p>
                </div>
            )}
        </Link>
    );
}
